import * as Sentry from '@sentry/nextjs';
import { v4 as uuidv4 } from 'uuid';

import {
  getProductCategories,
  getProductCategoryBySlug,
  getProductFamilyBySlug,
  getProductGroupBySlug,
} from '@/helpers/strapi/helpers';

import { replaceTextContentTemplate } from './strapi/utils';

let seoTexts = {};
try {
  seoTexts = require('../data/seoTexts.json');
} catch {
  // file not yet generated — all seoText props will be null
}

let locationSeoData = {};
try {
  locationSeoData = require('../data/locationSeoData.json');
} catch {
  // file not yet generated — cityData props will be null
}

let categorySeoData = {};
try {
  categorySeoData = require('../data/categorySeoData.json');
} catch {
  // file not yet generated — categoryData props will be null
}

const getCityData = slug => locationSeoData[slug] ?? null;
const getCategorySeoData = slug => categorySeoData[slug] ?? null;

const PRICE_BASIS_LABELS = {
  hour: 'Std.',
  day: 'Tag',
  week: 'Woche',
  month: 'Monat',
};

export const formatPriceLabel = (price, priceBasis) => {
  if (price == null) return null;
  const unit = PRICE_BASIS_LABELS[priceBasis] ?? 'Tag';
  return `ab ${price}\u00A0\u20AC/${unit}`;
};

export const LOCATION_ROUTES = {
  MAX_SEGMENTS: 4,
  SEGMENT_POSITIONS: {
    LOCATION: 0,
    PRODUCT_CATEGORY: 1,
    PRODUCT_FAMILY: 2,
    PRODUCT_GROUP: 3,
  },
  DEFAULT_CATEGORY_NAME: 'Bautechnik',
  LOCATIONS_LABEL: 'Standorte',
  LOCATION_OTHER_LABEL: 'Weitere Standorte',
  BASE_PATH: '/mieten/standorte',
  CATEGORY_NAME_PLACEHOLDER: '{{categoryName}}',
};

const mappers = {
  productTypes: productTypes => {
    return (
      productTypes?.map(type => ({
        headline: type.name,
        slug: type.slug,
        image: type.imageUrl,
        price: type.price,
        initialPrice: type.initialPrice,
        salesforceId: type.salesforceId,
        rentalPriceBasis: type.rentalPriceBasis,
      })) ?? []
    );
  },

  productGroups: (
    productGroups,
    locationSlug,
    productCategorySlug,
    productFamilySlug
  ) => {
    return (
      productGroups?.map(group => {
        const label = formatPriceLabel(
          group.minPrice?.price,
          group.minPrice?.priceBasis
        );
        return {
          headline: label
            ? `<span style="display:flex;align-items:baseline;gap:8px">${group.name}<span style="font-weight:400;font-size:0.875rem;color:#0065bd;margin-left:auto;white-space:nowrap">${label}</span></span>`
            : group.name,
          image: group.thumbnail,
          link: `${LOCATION_ROUTES.BASE_PATH}/${locationSlug}/${productCategorySlug}/${productFamilySlug}/${group.slug}`,
        };
      }) ?? []
    );
  },

  productFamilies: (productFamilies, locationSlug, productCategorySlug) => {
    return (
      productFamilies?.map(family => {
        const label = formatPriceLabel(
          family.minPrice?.price,
          family.minPrice?.priceBasis
        );
        return {
          headline: label
            ? `<span style="display:flex;align-items:baseline;gap:8px">${family.name}<span style="font-weight:400;font-size:0.875rem;color:#0065bd;margin-left:auto;white-space:nowrap">${label}</span></span>`
            : family.name,
          image: family.thumbnail,
          link: `${LOCATION_ROUTES.BASE_PATH}/${locationSlug}/${productCategorySlug}/${family.slug}`,
        };
      }) ?? []
    );
  },

  categories: (categories, locationSlug) => {
    return (
      categories?.map(category => ({
        headline: category.name,
        image: category.thumbnail,
        link: `${LOCATION_ROUTES.BASE_PATH}/${locationSlug}/${category.slug}`,
      })) ?? []
    );
  },
};

export const routeHandlers = {
  productGroup: async (
    productGroupSlug,
    productFamilySlug,
    productCategorySlug,
    location,
    siblingLocations = []
  ) => {
    try {
      const [
        {
          productGroupName,
          productFamilyName,
          productCategoryName,
          productTypes,
        },
        allCategories,
      ] = await Promise.all([
        getProductGroupBySlug(
          productGroupSlug,
          productFamilySlug,
          productCategorySlug
        ),
        getProductCategories(),
      ]);

      if (productTypes.length <= 0 || !productGroupName) {
        return { notFound: true };
      }

      const priceCatalog = {
        tile: [],
        dametisTile: productTypes.map(type => ({
          salesforceId: type.salesforceId,
          attributes: { data: [] },
        })),
        requestType: 'rental_request',
      };

      const groupText =
        seoTexts[location.slug]?.[`grp_${productGroupSlug}`] ?? null;
      const catSeoData = getCategorySeoData(productCategorySlug);
      const priced = productTypes.filter(t => t.price != null);
      const minType =
        priced.length > 0
          ? priced.reduce((a, b) => (a.price <= b.price ? a : b))
          : null;
      const minPrice = minType?.price ?? null;
      const minPriceBasis = minType?.rentalPriceBasis ?? null;

      return {
        props: {
          ...prepareSeoProps(location, productGroupName, groupText),
          locationName: location.city,
          locationSlug: location.slug,
          categoryName: productGroupName,
          categorySlug: productCategorySlug,
          cityData: getCityData(location.slug),
          categoryData: catSeoData,
          abPreisTag: minPrice,
          abPriceBasis: minPriceBasis,
          allCategories: allCategories.map(c => ({
            name: c.name,
            slug: c.slug,
          })),
          breadcrumbs: [
            prepareBreadcrumb(
              LOCATION_ROUTES.LOCATIONS_LABEL,
              LOCATION_ROUTES.BASE_PATH
            ),
            prepareBreadcrumb(
              location.city,
              `${LOCATION_ROUTES.BASE_PATH}/${location.slug}`
            ),
            prepareBreadcrumb(
              productCategoryName,
              `${LOCATION_ROUTES.BASE_PATH}/${location.slug}/${productCategorySlug}`
            ),
            prepareBreadcrumb(
              productFamilyName,
              `${LOCATION_ROUTES.BASE_PATH}/${location.slug}/${productCategorySlug}/${productFamilySlug}`
            ),
            prepareBreadcrumb(
              productGroupName,
              `${LOCATION_ROUTES.BASE_PATH}/${location.slug}/${productCategorySlug}/${productFamilySlug}/${productGroupSlug}`
            ),
          ],
          priceCatalog,
          hero: location?.hero ?? null,
          textContent: replaceTextContentTemplate(
            location.text,
            productGroupName
          ),
          seoText: groupText,
          siblingLocations,
          locationState: location.state,
        },
      };
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  },

  productFamily: async (
    productFamilySlug,
    productCategorySlug,
    location,
    siblingLocations = []
  ) => {
    try {
      const [
        {
          productFamilyName,
          productCategoryName,
          productGroups,
          familyMinPrice,
        },
        allCategories,
      ] = await Promise.all([
        getProductFamilyBySlug(productFamilySlug, productCategorySlug, {
          deep: true,
        }),
        getProductCategories(),
      ]);

      if (productGroups.length <= 0 || !productFamilyName) {
        return { notFound: true };
      }

      const groupCards = mappers.productGroups(
        productGroups,
        location?.slug,
        productCategorySlug,
        productFamilySlug
      );

      const familyText =
        seoTexts[location.slug]?.[`fam_${productFamilySlug}`] ?? null;
      const catSeoData = getCategorySeoData(productCategorySlug);

      return {
        props: {
          ...prepareSeoProps(location, productFamilyName, familyText),
          locationName: location.city,
          locationSlug: location.slug,
          categoryName: productFamilyName,
          categorySlug: productCategorySlug,
          cityData: getCityData(location.slug),
          categoryData: catSeoData,
          abPreisTag: familyMinPrice?.price ?? null,
          abPriceBasis: familyMinPrice?.priceBasis ?? null,
          allCategories: allCategories.map(c => ({
            name: c.name,
            slug: c.slug,
          })),
          breadcrumbs: [
            prepareBreadcrumb(
              LOCATION_ROUTES.LOCATIONS_LABEL,
              LOCATION_ROUTES.BASE_PATH
            ),
            prepareBreadcrumb(
              location.city,
              `${LOCATION_ROUTES.BASE_PATH}/${location.slug}`
            ),
            prepareBreadcrumb(
              productCategoryName,
              `${LOCATION_ROUTES.BASE_PATH}/${location.slug}/${productCategorySlug}`
            ),
            prepareBreadcrumb(
              productFamilyName,
              `${LOCATION_ROUTES.BASE_PATH}/${location.slug}/${productCategorySlug}/${productFamilySlug}`
            ),
          ],
          categoryCards: groupCards,
          hero: location?.hero ?? null,
          textContent: replaceTextContentTemplate(
            location.text,
            productFamilyName
          ),
          seoText: familyText,
          siblingLocations,
          locationState: location.state,
        },
      };
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  },

  category: async (productCategorySlug, location, siblingLocations = []) => {
    try {
      const [
        { productCategoryName, productFamilies, categoryMinPrice },
        allCategories,
      ] = await Promise.all([
        getProductCategoryBySlug(productCategorySlug, { deep: true }),
        getProductCategories(),
      ]);

      if (!productCategoryName || productFamilies.length <= 0) {
        return { notFound: true };
      }

      const categoryCards = mappers.productFamilies(
        productFamilies,
        location?.slug,
        productCategorySlug
      );

      const categoryText =
        seoTexts[location.slug]?.[`cat_${productCategorySlug}`] ?? null;
      const catSeoData = getCategorySeoData(productCategorySlug);

      return {
        props: {
          ...prepareSeoProps(location, productCategoryName, categoryText),
          locationName: location.city,
          locationSlug: location.slug,
          categoryName: productCategoryName,
          categorySlug: productCategorySlug,
          cityData: getCityData(location.slug),
          categoryData: catSeoData,
          abPreisTag: categoryMinPrice?.price ?? null,
          abPriceBasis: categoryMinPrice?.priceBasis ?? null,
          allCategories: allCategories.map(c => ({
            name: c.name,
            slug: c.slug,
          })),
          categoryCards,
          hero: location?.hero ?? null,
          breadcrumbs: [
            prepareBreadcrumb(
              LOCATION_ROUTES.LOCATIONS_LABEL,
              LOCATION_ROUTES.BASE_PATH
            ),
            prepareBreadcrumb(
              location.city,
              `${LOCATION_ROUTES.BASE_PATH}/${location.slug}`
            ),
            prepareBreadcrumb(
              productCategoryName,
              `${LOCATION_ROUTES.BASE_PATH}/${location.slug}/${productCategorySlug}`
            ),
          ],
          textContent: replaceTextContentTemplate(
            location.text,
            productCategoryName
          ),
          seoText: categoryText,
          siblingLocations,
          locationState: location.state,
        },
      };
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  },

  location: async (location, siblingLocations = []) => {
    try {
      const categories = await getProductCategories();
      const categoryCards = mappers.categories(categories, location?.slug);
      const cityText = seoTexts[location.slug]?.cityContextText ?? null;

      return {
        props: {
          ...prepareSeoProps(location, undefined, cityText),
          locationName: location.city,
          locationSlug: location.slug,
          categoryName: LOCATION_ROUTES.DEFAULT_CATEGORY_NAME,
          categorySlug: null,
          cityData: getCityData(location.slug),
          categoryData: null,
          abPreisTag: null,
          abPriceBasis: null,
          allCategories: categories.map(c => ({ name: c.name, slug: c.slug })),
          categoryCards,
          breadcrumbs: [
            prepareBreadcrumb(
              LOCATION_ROUTES.LOCATIONS_LABEL,
              LOCATION_ROUTES.BASE_PATH
            ),
            prepareBreadcrumb(
              location.city,
              `${LOCATION_ROUTES.BASE_PATH}/${location.slug}`
            ),
          ],
          hero: location?.hero ?? null,
          textContent: replaceTextContentTemplate(
            location?.text,
            LOCATION_ROUTES.DEFAULT_CATEGORY_NAME
          ),
          seoText: cityText,
          siblingLocations,
          locationState: location.state,
        },
      };
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  },
};

const prepareSeoProps = (location, categoryName, seoText = null) => {
  const metaTitle = categoryName
    ? `${categoryName} mieten in ${location.city}`
    : `${LOCATION_ROUTES.DEFAULT_CATEGORY_NAME} mieten in ${location.city}`;
  const metaDescription =
    seoText ??
    (categoryName
      ? `Entdecken Sie unsere Auswahl an ${categoryName} am Standort ${location.city}. ✓ persönlicher Ansprechpartner ✓ geprüfte Vermietpartner ✓ schneller Mietprozess ► Jetzt anfragen!`
      : `Entdecken Sie unsere Auswahl an ${LOCATION_ROUTES.DEFAULT_CATEGORY_NAME} am Standort ${location.city}. ✓ persönlicher Ansprechpartner ✓ geprüfte Vermietpartner ✓ schneller Mietprozess ► Jetzt anfragen!`);
  return {
    seo: {
      title: metaTitle,
      description: metaDescription,
    },
  };
};

export const parseSlug = slug => {
  const { SEGMENT_POSITIONS } = LOCATION_ROUTES;

  return {
    locationSlug: slug[SEGMENT_POSITIONS.LOCATION],
    productCategorySlug: slug[SEGMENT_POSITIONS.PRODUCT_CATEGORY],
    productFamilySlug: slug[SEGMENT_POSITIONS.PRODUCT_FAMILY],
    productGroupSlug: slug[SEGMENT_POSITIONS.PRODUCT_GROUP],
  };
};

export const validateSlugLength = slug => {
  if (slug.length > LOCATION_ROUTES.MAX_SEGMENTS) {
    return { notFound: true };
  }
  return null;
};

const prepareBreadcrumb = (label, slug) => {
  return {
    id: uuidv4(),
    title: label,
    metadata: {
      id: uuidv4(),
      pathname: slug,
    },
  };
};
